import {
    Directive,
    Input,
    ElementRef,
    ViewContainerRef,
    Injector,
    ComponentRef,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { LoadingIndicatorComponent } from '../components/loadingIndicator/loading-indicator.component';

@Directive({
    selector: '[agprLoading]',
    standalone: false
})
export class LoadingDirective implements OnChanges {

    @Input('agprLoading') loading: boolean = false;
    /* eslint-disable @angular-eslint/no-input-rename */
    @Input('agprInlineLoadingIndicator') isInlineIndicator: boolean  = false;
    @Input('agprGlobalLoading') useWholeViewPort: boolean  = false;
    @Input('loadingIndicatorStyleClass') styleClass: string | object = null;
    @Input('loadingLightMode') lightMode: boolean = false;
    @Input('loadingSmallIndicator') smallIndicator: boolean = false;
    @Input('loadingMessage') loadingMessage: string;

    @Input() overlayStyleClass: string | object = null;
    @Input() dotStyleClass: string | object = null;

    loadingIndicator: ComponentRef<LoadingIndicatorComponent> = null;

    constructor(
        private element: ElementRef,
        private viewContainerRef: ViewContainerRef,
        private injector: Injector,
    ) {}

    /**
    * When inputs change, update them in the loading indicator component
    */
    ngOnChanges(changes: SimpleChanges) {
        const loadingStatusChanged: boolean = changes.loading !== null && changes.loading !== undefined;
        if (loadingStatusChanged) {
            this.toggleLoading();
        }

        this.setLoadingIndicatorInputs();
    }

    /**
    * Display or hide the loader
    */
    private toggleLoading(): void {
        if (this.loading) {
            this.enableLoading();
        } else {
            this.disableLoading();
        }
    }

    /**
    * If the loader has not been created,
    * create it and then display it
    */
    private enableLoading(): void {
        if (!this.loaderReady()) {
            this.delayedCreate(50);
        } else {
            this.enableOverlay();
            this.loadingIndicator.instance.loading = true;
        }

        this.element.nativeElement.setAttribute('aria-busy', true);
        this.element.nativeElement.classList.add('u-relative');
    }

    /**
    * Hide the loader
    */
    private disableLoading(): void {
        if (this.loadingIndicator) {
            this.loadingIndicator.instance.loading = false;
            this.disableOverlay();
        }

        this.element.nativeElement.setAttribute('aria-busy', false);
        this.element.nativeElement.classList.remove('u-relative');
    }

    /**
    * Enable the overlay after a small delay so that it can animate in
    */
    private enableOverlay(): void {
        this.loadingIndicator.instance.overlayActive = true;
    }

    /**
    * Hide the overlay
    */
    private disableOverlay(): void {
        this.loadingIndicator.instance.overlayActive = false;
    }

    private delayedCreate(delay: number): void {
        setTimeout(() => this.createLoader(), delay);
    }

    /**
    * Create a new instance of the LoaderInstanceComponent,
    * add it to the DOM, and display it
    */
    private createLoader(): void {
        const parentElement: any = this.element.nativeElement;

        try {
            const loadingIndicator: ComponentRef<LoadingIndicatorComponent> = this.viewContainerRef.createComponent(
                LoadingIndicatorComponent,
                {
                    index: 0,
                    injector: this.injector
                }
            );

            const loadingIndicatorElement: any = loadingIndicator.location.nativeElement;

            loadingIndicator.changeDetectorRef.markForCheck();

            parentElement.appendChild(loadingIndicatorElement);

            this.loadingIndicator = loadingIndicator;

            this.setLoadingIndicatorInputs();

            this.enableOverlay();
        } catch (err) {
            // Some error creating the loading indicator likely, ignore it, can't show loading on something that doesn't exist anymore
        }
    }

    private setLoadingIndicatorInputs(): void {
        const hasLoadingIndicataor: boolean = this.loadingIndicator !== null && this.loadingIndicator !== undefined;

        if (hasLoadingIndicataor) {
            this.loadingIndicator.instance.loading = this.loading;
            this.loadingIndicator.instance.inline = this.isInlineIndicator;
            this.loadingIndicator.instance.useWholeViewPort = this.useWholeViewPort;
            this.loadingIndicator.instance.styleClass = this.styleClass;
            this.loadingIndicator.instance.overlayStyleClass = this.overlayStyleClass;
            this.loadingIndicator.instance.dotStyleClass = this.dotStyleClass;
            this.loadingIndicator.instance.lightMode = this.lightMode;
            this.loadingIndicator.instance.smallIndicator = this.smallIndicator;
            this.loadingIndicator.instance.loadingMessage = this.loadingMessage;
        }
    }

    /**
    * Determine if the loader instance exists and if it is inserted in the DOM
    */
    private loaderReady(): boolean {
        const parentElement: any = this.element.nativeElement;
        let loadingIndicatorElement: any;
        const hasLoadingIndicataor: boolean = this.loadingIndicator !== null && this.loadingIndicator !== undefined;
        let loaderReady: boolean;

        if (hasLoadingIndicataor) {
            loadingIndicatorElement = this.loadingIndicator.location.nativeElement;
            loaderReady = this.loadingIndicator && loadingIndicatorElement.parentNode === parentElement;
        } else {
            loaderReady = false;
        }

        return loaderReady;
    }
}
